import { faDiagramProject } from "@fortawesome/free-solid-svg-icons/faDiagramProject";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import isEmpty from "lodash/isEmpty";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { CreateCampaignDripDto } from "../../../model/campaign/dto/CreateCampaignDripDto";
import { campaignSliceSelectors } from "../../../redux/campaign/campaign.selector";
import { useAppSelector } from "../../../redux/store";
import { Spacing } from "../../../theme/Spacing";

import { DripCampaignStep } from "../components/DripCampaignStep";
import { Info } from "../../../components/Info";

const Container = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xl};
  width: 100%;
  flex-wrap: wrap;
  align-self: flex-start;
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  list-style-type: disc;
  list-style-position: inside;
`;

const AddStepButton = styled(Button)`
  margin: 0;
  border: ${({ theme }) => `1px solid ${theme.main}`};
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  margin-top: ${Spacing.m};
  width: fit-content;
  padding-inline: ${Spacing.xxl};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.sub};
  }
`;

export const DripCampaignCreateEditStepSteps: React.FC = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "campaigns.dripCampaign.step.campaignSteps",
  });

  const { control } = useFormContext<CreateCampaignDripDto>();

  const selectedCampaign = useAppSelector(
    campaignSliceSelectors.selectSelectedCampaign,
  );

  const editMode =
    !isEmpty(selectedCampaign) && !isEmpty(selectedCampaign?._id);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "steps",
  });

  const nextStepNumber =
    Math.max(0, ...fields.map((field) => field.stepNumber ?? 0)) + 1;

  return (
    <Container>
      <Flex flexDirection="column" gap="l" width="100%">
        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={faDiagramProject} color="main" size="xl" />

          <Text fontSize="l" fontWeight={"bold"} color="main">
            {t("title")}
          </Text>
        </Flex>

        <Info
          info={
            <>
              {t("description")}{" "}
              <Trans
                i18nKey="campaigns.dripCampaign.step.campaignSteps.description.2"
                components={[<strong key="0" />]}
              />
            </>
          }
        />

        {editMode ? (
          <Info
            color="warning"
            align="flex-start"
            info={
              <>
                {t("edit.info.title")}

                <StyledList>
                  <li>{t("edit.info.wording")}</li>
                  <li>{t("edit.info.timing")}</li>
                  <li>{t("edit.info.newStep")}</li>
                </StyledList>
              </>
            }
          />
        ) : null}

        {fields.length
          ? fields.map((f, i) => (
              <DripCampaignStep key={f.id} index={i} remove={remove} />
            ))
          : null}

        <AddStepButton
          disabled={fields.length >= 3}
          icon={faPlus}
          // dataTestId={TestIDs.forms.createEdit.steps.step1.domain.addButton}
          onClick={() =>
            append({
              subject: "",
              body: "",
              // Not fields.length + 1: removing a middle step leaves a gap, and
              // counting would hand the new step a number an existing one still
              // holds. The backend keys what it has sent on stepNumber, so a
              // repeat means one of the two emails is never sent
              stepNumber: nextStepNumber,
              hoursAfter: 0,
              ctaText: "",
              ctaUrl: "",
              ctaIcon: undefined,
            })
          }
        >
          {t("add")}
        </AddStepButton>
      </Flex>
    </Container>
  );
};
